import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext, useAuth } from "@/contexts/AuthContext";
import { API, axios, resetSessionExpiredFlag } from "@/api";
import { Loader2 } from "lucide-react";
import { useGeolocation } from "@/hooks/useGeolocation";
import { identify as mpIdentify, reset as mpReset } from "@/lib/mixpanel";
import { identify as phIdentify, reset as phReset } from "@/lib/posthog";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const geo = useGeolocation();

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Request GPS once user is logged in
  useEffect(() => {
    if (user && geo.permission === "prompt") {
      geo.requestLocation();
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    resetSessionExpiredFlag();
    setToken(res.data.access_token);
    setUser(res.data.user);
    mpIdentify(res.data.user?.id, { email, name: res.data.user?.first_name });
    phIdentify(res.data.user?.id, { name: res.data.user?.first_name, role: res.data.user?.role });
    return res.data;
  };

  const register = async (userData) => {
    const res = await axios.post(`${API}/auth/register`, userData);
    localStorage.setItem("token", res.data.access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    resetSessionExpiredFlag();
    setToken(res.data.access_token);
    setUser(res.data.user);
    mpIdentify(res.data.user?.id, { email: userData.email, name: userData.first_name });
    phIdentify(res.data.user?.id, { name: userData.first_name, role: res.data.user?.role });
    return res.data;
  };

  const logout = async () => {
    // Mobile-safe: cap the backend call so a flaky/dead connection cannot
    // stall the sign-out flow. localStorage clear + state reset still run
    // unconditionally below, so the user is signed out client-side even
    // if the network call never returns.
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        { withCredentials: true, timeout: 3000 },
      );
    } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    mpReset();
    phReset();
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  // Vendor live session heartbeat — refresh token every 10 min while vendor is live
  useEffect(() => {
    if (!user || user.role !== "vendor") return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/vendor/dashboard`);
        if (res.data?.session_status === "live") {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        }
      } catch {}
    }, 10 * 60 * 1000); // 10 minutes
    return () => clearInterval(interval);
  }, [user?.role]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, fetchUser, updateUser, refreshUser: fetchUser, geo }}>
      {children}
    </AuthContext.Provider>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  if (!token) return <Navigate to="/onboarding" replace />;
  return children;
};


export const AdminRoute = ({ children }) => {
  const { token, user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  if (!token) return <Navigate to="/onboarding" replace />;
  if (!user?.is_admin) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-center p-8"><p className="text-lg font-semibold text-red-600" data-testid="admin-denied">403 — Admin access required</p><a href="/" className="text-sky-500 underline mt-2 block">Go home</a></div></div>;
  return children;
};
